import React, { useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { maxHeight, spacing } from '@mui/system';
import styled from 'styled-components';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentIcon from '@mui/icons-material/Payment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import HomeIcon from '@mui/icons-material/Home';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import ChatIcon from '@mui/icons-material/Chat';
import ReplyIcon from '@mui/icons-material/Reply';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import moment from 'moment';
import { getDisplayCreatorName } from '../../../utils/noteUtils';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  dateFormatter,
  dateTimeLocal,
  formatMoney
} from '../../../setup/helpers';
import {
  createNewNote,
  createNewNoteReply,
  deleteNoteOrReply,
  updateNote,
  setNotes,
  getAccountNotesByAccountUUID
} from '../api/actions';
import { setSnackbar } from '../../snackbar/api/snackbar';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  table: {
    minWidth: 750,
  },
  tableContainer: {
    maxHeight: 'calc(100vh - 400px)',
    minHeight: 200,
  },
  tableHead: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  tableHeadCell: {
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  noteTypeChip: {
    margin: theme.spacing(0.5),
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 500,
    '& .MuiChip-icon': {
      fontSize: '0.875rem',
      color: 'inherit',
      marginLeft: 6,
      marginRight: -4,
    },
    '& .MuiChip-label': {
      padding: '0 8px',
    },
  },
  quickActionChip: {
    cursor: 'pointer',
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    color: '#333',
    fontWeight: 500,
    fontSize: '0.8125rem',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
    '& .MuiChip-icon': {
      color: 'inherit',
      marginLeft: 8,
    },
    '& .MuiChip-label': {
      padding: '0 12px',
    },
  },
  replyButton: {
    marginLeft: theme.spacing(1),
  },
  chatModal: {
    minWidth: 600,
    maxWidth: 800,
  },
  chatMessage: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
  },
  chatMessageOwn: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    marginLeft: theme.spacing(4),
  },
  chatMessageOther: {
    backgroundColor: theme.palette.grey[100],
    marginRight: theme.spacing(4),
  },
  quickActionModal: {
    minWidth: 500,
  },
  searchBar: {
    marginBottom: theme.spacing(2),
  },
  pagination: {
    marginTop: theme.spacing(2),
    '& .MuiTablePagination-toolbar': {
      color: theme.palette.text.primary,
    },
    '& .MuiTablePagination-selectIcon': {
      color: theme.palette.text.primary,
    },
    '& .MuiTablePagination-actions button': {
      color: theme.palette.text.primary,
    },
  },
}));

// Transition component for Dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Quick action note types - using component references instead of JSX
const quickActionNotes = [
  {
    type: 'General Note',
    display: 'Balance Inquiry',
    icon: AccountBalanceIcon,
    color: '#1976d2', // Primary blue
    message: 'Customer inquired about account balance'
  },
  {
    type: 'Payment Only',
    display: 'Payment Only',
    icon: PaymentIcon,
    color: '#2e7d32', // Success green
    message: 'Payment received and processed'
  },
  {
    type: 'General Note',
    display: 'Payment Inquiry',
    message: 'Explained payment options to customer',
    icon: AttachMoneyIcon,
    color: '#1976d2' // Primary blue
  },
  {
    type: 'General Note',
    display: 'Past Due Inquiry',
    icon: ReportProblemIcon,
    color: '#1976d2', // Primary blue
    message: 'Customer called about past due balance'
  },
  {
    type: 'Service Problem',
    display: 'Outage Reported',
    message: 'Customer has called to report outage',
    icon: FlashOnIcon,
    color: '#d32f2f' // Error red
  },
  {
    type: 'Move In/Out Inquiry',
    display: 'Move Out Process',
    message: 'Explained to customer stop service process',
    icon: HomeIcon,
    color: '#7b1fa2' // Purple
  }
];

const AccountNotesTable = ({ notes, account, user, bills, payments, apl, createNewNote, createNewNoteReply, deleteNoteOrReply, updateNote, setNotes, getAccountNotesByAccountUUID }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  
  // State management
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [quickActionModalOpen, setQuickActionModalOpen] = useState(false);
  const [selectedQuickAction, setSelectedQuickAction] = useState(null);
  const [newReply, setNewReply] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [completedModalOpen, setCompletedModalOpen] = useState(false);
  const [selectedNoteForCompletion, setSelectedNoteForCompletion] = useState(null);
  const [dueDate, setDueDate] = useState('');
  const [expandedNoteIds, setExpandedNoteIds] = useState(new Set());
  const [newNoteDueDate, setNewNoteDueDate] = useState('');
  const [selectedNoteType, setSelectedNoteType] = useState('');

  // Helper functions
  const canEditOrDeleteNote = (note) => {
    // Can only edit/delete if user is the creator, it's the most recent note, AND it's less than 24 hours old
    if (!notes.data || notes.data.length === 0) return false;
    const mostRecentNote = notes.data[0]; // Assuming notes are sorted by creation date desc
    
    // Check if user is the creator and it's the most recent note
    const isCreatorAndMostRecent = note.id === mostRecentNote.id && 
                                    note.creator?.id === user.details?.id;
    
    if (!isCreatorAndMostRecent) return false;
    
    // Check if note is less than 24 hours old
    if (!note.created) return false;
    const noteCreatedTime = moment(note.created);
    const hoursSinceCreation = moment().diff(noteCreatedTime, 'hours');
    const isLessThan24HoursOld = hoursSinceCreation < 24;
    
    return isLessThan24HoursOld;
  };

  const getNoteTopicIdByType = (noteType) => {
    // Default to 34 (General Note) if no match found
    let noteTopicId = 34;
    
    if (apl.genericTypes?.noteTopic?.data && apl.genericTypes.noteTopic.data.length > 0) {
      const matchingTopic = apl.genericTypes.noteTopic.data.find(item => 
        item.description === noteType
      );
      if (matchingTopic) {
        noteTopicId = matchingTopic.id;
      }
    }
    
    return noteTopicId;
  };

  const generateDynamicNoteMessage = (actionIndex) => {
    const action = quickActionNotes[actionIndex];
    
    switch(actionIndex) {
      case 0: // Balance inquiry
        if (account.data?.ongoingAccountBalance?.balance > 0) {
          return `Customer requested balance of ${formatMoney(account.data.ongoingAccountBalance.balance)} ${bills.data?.items?.[0]?.dueDate ? `due ${moment(bills.data.items[0].dueDate).format("MM-DD-YY")}` : ''}`;
        } else {
          return `Customer requested balance of ${formatMoney(account.data?.ongoingAccountBalance?.balance || 0)}`;
        }
      case 1: // Payment Only
        const lastPaymentAmount = account.data?.ongoingAccountBalance?.lastPaymentAmount || 0;
        const paymentId = payments.data?.items?.[0]?.id || 'N/A';
        return `Credit/Debit Card or E-Check payment #${paymentId} ${formatMoney(lastPaymentAmount)} (including fee)`;
      case 3: // Past Due Inquiry
        if (account.data?.ongoingAccountBalance?.pastDue > 0) {
          return `Customer requested past due balance of ${formatMoney(account.data.ongoingAccountBalance.pastDue)} ${bills.data?.items?.[0]?.dueDate ? `due ${moment(bills.data.items[0].dueDate).format("MM-DD-YY")}` : ''}`;
        } else {
          return `Customer requested past due balance of ${formatMoney(account.data?.ongoingAccountBalance?.pastDue || 0)}`;
        }
      default:
        return action.message || action.display;
    }
  };

  // Filter notes based on search query
  const filteredNotes = (notes.data || []).filter(note => {
    const displayCreatorName = getDisplayCreatorName(note.creatorName);
    return note.note?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.noteTopicType?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      displayCreatorName?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Chat modal functions
  const handleOpenChat = (note) => {
    setSelectedNote(note);
    setChatModalOpen(true);
  };

  const handleCloseChat = () => {
    setChatModalOpen(false);
    setSelectedNote(null);
    setNewReply('');
  };

  const handleSendReply = () => {
    if (newReply.trim() && selectedNote) {
      const replyData = {
        reply: newReply,
        noteId: selectedNote.id
      };
      createNewNoteReply(replyData, user.details)
        .then(() => {
          setNewReply('');
          dispatch(setSnackbar(true, 'success', 'Reply sent successfully'));
        })
        .catch(() => {
          dispatch(setSnackbar(true, 'error', 'Failed to send reply'));
        });
    }
  };

  // Quick action functions
  const handleQuickActionClick = (action) => {
    const actionIndex = quickActionNotes.findIndex(a => a.display === action.display);
    const dynamicMessage = generateDynamicNoteMessage(actionIndex);
    setSelectedQuickAction(action);
    setEditNoteText(dynamicMessage);
    setNewNoteDueDate('');
    setSelectedNoteType(action.type || '');
    setQuickActionModalOpen(true);
  };

  const handleNewNoteClick = () => {
    setSelectedQuickAction(null);
    setEditNoteText('');
    setNewNoteDueDate('');
    setSelectedNoteType('');
    setQuickActionModalOpen(true);
  };

  const handleQuickActionSubmit = () => {
    if (editNoteText.trim() && account.data.account) {
      // Determine note topic ID - use selectedNoteType if available, otherwise use quick action type, otherwise default to General Note
      const noteTypeToUse = selectedNoteType || (selectedQuickAction ? selectedQuickAction.type : 'General Note');
      const noteTopicId = getNoteTopicIdByType(noteTypeToUse);
        
      // Validation: Check if note topics are loaded
      if (!apl.genericTypes?.noteTopic?.data || apl.genericTypes.noteTopic.data.length === 0) {
        dispatch(setSnackbar(true, 'error', 'Note topics not loaded. Please refresh the page and try again.'));
        return;
      }
        
      const newNote = {
        note: editNoteText,
        noteTopicId: noteTopicId,
        accountId: account.data.account.id,
        completed: 1 // Notes are completed by default
      };
      
      // Only include dueDate if it's provided
      if (newNoteDueDate && newNoteDueDate.trim() !== '') {
        newNote.dueDate = newNoteDueDate;
      }
      
      console.log('Creating note with valid topic ID:', noteTopicId);
      createNewNote(newNote, user.details);
      setQuickActionModalOpen(false);
      setSelectedQuickAction(null);
      setEditNoteText('');
      setNewNoteDueDate('');
      setSelectedNoteType('');
    }
  };

  // Edit note functions
  const handleEditNote = (note) => {
    setEditingNote(note);
    setEditNoteText(note.note);
  };

  const handleSaveEdit = () => {
    if (editingNote && editNoteText.trim()) {
      const updatedNote = { ...editingNote, note: editNoteText };
      updateNote(updatedNote, user.details)
        .then(() => {
          setEditingNote(null);
          setEditNoteText('');
          dispatch(setSnackbar(true, 'success', 'Note updated successfully'));
          const accountUuid = account.data?.account?.uuid;
          if (accountUuid) {
            getAccountNotesByAccountUUID(accountUuid, false);
          }
        })
        .catch(() => {
          dispatch(setSnackbar(true, 'error', 'Failed to update note'));
        });
    }
  };

  // Delete note function
  const handleDeleteNote = (note) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      deleteNoteOrReply(note, user.details)
        .then(() => {
          // Manually remove the note from the local state
          const updatedNotes = notes.data.filter(n => n.id !== note.id);
          setNotes(updatedNotes);
          dispatch(setSnackbar(true, 'success', 'Note deleted successfully'));
        })
        .catch(() => {
          dispatch(setSnackbar(true, 'error', 'Failed to delete note'));
        });
    }
  };

  // Delete reply function
  const handleDeleteReply = (reply, replyIndex) => {
    if (window.confirm('Are you sure you want to delete this reply?')) {
      deleteNoteOrReply(reply, user.details)
        .then(() => {
          // Manually remove the reply from the local state
          const updatedNotes = notes.data.map(note => {
            if (note.id === selectedNote.id) {
              return {
                ...note,
                replys: note.replys.filter((_, index) => index !== replyIndex)
              };
            }
            return note;
          });
          setNotes(updatedNotes);
          
          // Update the selected note for the modal
          const updatedSelectedNote = {
            ...selectedNote,
            replys: selectedNote.replys.filter((_, index) => index !== replyIndex)
          };
          setSelectedNote(updatedSelectedNote);
          
          dispatch(setSnackbar(true, 'success', 'Reply deleted successfully'));
        })
        .catch(() => {
          dispatch(setSnackbar(true, 'error', 'Failed to delete reply'));
        });
    }
  };

  // Completed status functions
  const handleCompletedToggle = (note) => {
    setSelectedNoteForCompletion(note);
    setDueDate(note.dueDate || '');
    setCompletedModalOpen(true);
  };

  const handleCompletedSubmit = () => {
    if (selectedNoteForCompletion) {
      const newCompletedStatus = selectedNoteForCompletion.completed ? 0 : 1;
      const updatedNote = { 
        ...selectedNoteForCompletion, 
        completed: newCompletedStatus,
        dueDate: newCompletedStatus === 0 ? dueDate : null // If marking as incomplete (0), set due date; if completed (1), clear due date
      };
      updateNote(updatedNote, user.details)
        .then(() => {
          setCompletedModalOpen(false);
          setSelectedNoteForCompletion(null);
          setDueDate('');
          dispatch(setSnackbar(true, 'success', newCompletedStatus === 1 ? 'Note marked as completed' : 'Note marked as incomplete'));
          const accountUuid = account.data?.account?.uuid;
          if (accountUuid) {
            getAccountNotesByAccountUUID(accountUuid, false);
          }
        })
        .catch(() => {
          dispatch(setSnackbar(true, 'error', 'Failed to update note status'));
        });
    }
  };

  // Get icon for note type
  const getNoteTypeIcon = (noteTopicType) => {
    const action = quickActionNotes.find(a => a.type === noteTopicType);
    if (action) {
      const IconComponent = action.icon;
      return <IconComponent />;
    }
    return <PersonIcon />;
  };

  // Get color for note type
  const getNoteTypeColor = (noteTopicType) => {
    const action = quickActionNotes.find(a => a.type === noteTopicType);
    return action ? action.color : '#666';
  };

  // Toggle note expansion
  const handleToggleNoteExpansion = (noteId) => {
    setExpandedNoteIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  };

  return (
    <Card>
      <CardContent>
        {/* Header with Quick Actions */}
        <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" component="h2">
              Account Notes
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box 
                display="flex" 
                justifyContent="flex-end" 
                flexWrap="wrap" 
                alignItems="center" 
                gap={1.5}
              >
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleNewNoteClick}
              >
                New Note
              </Button>
              {quickActionNotes.map((action, index) => {
                const IconComponent = action.icon;
                return (
                  <Tooltip key={index} title={`Quick: ${action.message}`}>
                    <Chip
                      icon={action.type !== 'General Note' ? <IconComponent fontSize="small" /> : null}
                      label={action.display}
                      className={classes.quickActionChip}
                      style={{
                        backgroundColor: `${action.color}15`,
                        color: action.color,
                      }}
                      onClick={() => handleQuickActionClick(action)}
                      variant="outlined"
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Grid>
        </Grid>

        {/* Search Bar */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search notes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={classes.searchBar}
          size="small"
        />

        {/* Notes Table */}
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table className={classes.table} stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={`${classes.tableHead} ${classes.tableHeadCell}`}>
                  Type
                </TableCell>
                <TableCell className={`${classes.tableHead} ${classes.tableHeadCell}`}>
                  Note
                </TableCell>
                <TableCell className={`${classes.tableHead} ${classes.tableHeadCell}`}>
                  Created By
                </TableCell>
                <TableCell className={`${classes.tableHead} ${classes.tableHeadCell}`}>
                  Date
                </TableCell>
                <TableCell className={`${classes.tableHead} ${classes.tableHeadCell}`}>
                  Status
                </TableCell>
                <TableCell className={`${classes.tableHead} ${classes.tableHeadCell}`}>
                  Replies
                </TableCell>
                <TableCell className={`${classes.tableHead} ${classes.tableHeadCell}`}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notes.isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" p={3}>
                      <CircularProgress size={24} style={{ marginRight: 8 }} />
                      <Typography>Loading notes...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : filteredNotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box p={3}>
                      <Typography variant="body1" color="textSecondary">
                        {searchQuery ? 'No notes found matching your search.' : 'No notes found for this account.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredNotes
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((note, index) => (
                    <TableRow key={note.id || index} className={classes.tableRow}>
                      <TableCell>
                        <Chip
                          icon={!note.noteTopicType?.includes('General Note') ? getNoteTypeIcon(note.noteTopicType) : null}
                          label={note.noteTopicType || 'General Note'}
                          className={classes.noteTypeChip}
                          style={{
                            backgroundColor: `${getNoteTypeColor(note.noteTopicType)}20`,
                            color: getNoteTypeColor(note.noteTopicType),
                            borderColor: `${getNoteTypeColor(note.noteTopicType)}50`,
                          }}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography 
                          onClick={() => handleToggleNoteExpansion(note.id)} 
                          variant="body2" 
                          noWrap={!expandedNoteIds.has(note.id)} 
                          style={{ 
                            maxWidth: expandedNoteIds.has(note.id) ? 'none' : 300,
                            cursor: 'pointer',
                            whiteSpace: expandedNoteIds.has(note.id) ? 'normal' : 'nowrap',
                            wordBreak: expandedNoteIds.has(note.id) ? 'break-word' : 'normal'
                          }}
                        >
                          {note.note}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar style={{ width: 24, height: 24, marginRight: 8 }}>
                            {getDisplayCreatorName(note.creatorName)?.charAt(0) || 'S'}
                          </Avatar>
                          <Typography variant="body2">
                            {getDisplayCreatorName(note.creatorName)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {note.created ? moment(note.created).format('MM/DD/YYYY HH:mm') : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            size="small"
                            label={note.completed ? 'Completed' : 'Pending'}
                            style={{
                              backgroundColor: note.completed ? '#4caf50' : '#ff9800',
                              color: 'white'
                            }}
                          />
                          {note.dueDate && !note.completed && (
                            <Typography variant="caption" color="error">
                              Due: {moment(note.dueDate).format('MM/DD/YY')}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Typography variant="body2" style={{ marginRight: 8 }}>
                            {note.replys?.length || 0}
                          </Typography>
                          {(note.replys?.length > 0) && (
                            <Button
                              size="small"
                              startIcon={<ChatIcon />}
                              onClick={() => handleOpenChat(note)}
                              className={classes.replyButton}
                            >
                              View
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex">
                          <Tooltip title="Reply">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenChat(note)}
                            >
                              <ReplyIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Toggle Completed">
                            <IconButton
                              size="small"
                              onClick={() => handleCompletedToggle(note)}
                            >
                              <EventIcon />
                            </IconButton>
                          </Tooltip>
                          {canEditOrDeleteNote(note) && (
                            <>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditNote(note)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteNote(note)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredNotes.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          className={classes.pagination}
        />

        {/* Chat Modal */}
        <Dialog
          open={chatModalOpen}
          TransitionComponent={Transition}
          fullWidth={true}
          maxWidth="md"
          keepMounted
          onClose={handleCloseChat}
          aria-labelledby="chat-dialog-title"
          aria-describedby="chat-dialog-description"
        >
          <DialogTitle>
            <Box display="flex" alignItems="center">
              <Chip
                icon={selectedNote?.noteTopicType && !selectedNote.noteTopicType.includes('General Note') ? getNoteTypeIcon(selectedNote.noteTopicType) : null}
                label={selectedNote?.noteTopicType || 'Note'}
                style={{ 
                  backgroundColor: selectedNote ? `${getNoteTypeColor(selectedNote.noteTopicType)}15` : 'rgba(102, 102, 102, 0.15)',
                  color: selectedNote ? getNoteTypeColor(selectedNote.noteTopicType) : '#666',
                  marginRight: 16,
                  height: 32,
                  fontSize: '0.8125rem'
                }}
                variant="outlined"
              />
              <Typography variant="h6">
                {selectedNote?.noteTopicType || 'Note'} - {getDisplayCreatorName(selectedNote?.creatorName)}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box mb={2}>
              <Typography variant="body1" style={{ fontWeight: 500 }}>
                {selectedNote?.note}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {selectedNote?.created ? moment(selectedNote.created).format('MM/DD/YYYY HH:mm') : 'N/A'}
              </Typography>
            </Box>
            
            <Divider style={{ margin: '16px 0' }} />
            
                             {/* Replies */}
             {selectedNote?.replys?.map((reply, index) => (
               <Box
                 key={index}
                 className={`${classes.chatMessage} ${
                   reply.creatorName === user.details?.firstName + ', ' + user.details?.lastName
                     ? classes.chatMessageOwn
                     : classes.chatMessageOther
                 }`}
               >
                 <Box display="flex" alignItems="center" mb={1}>
                   <Avatar style={{ width: 20, height: 20, marginRight: 8 }}>
                     {getDisplayCreatorName(reply.creatorName)?.charAt(0) || 'S'}
                   </Avatar>
                   <Typography variant="body2" style={{ fontWeight: 500 }}>
                     {getDisplayCreatorName(reply.creatorName)}
                   </Typography>
                   <Typography variant="caption" style={{ marginLeft: 'auto' }}>
                     {reply.created ? moment(reply.created).format('MM/DD/YYYY HH:mm') : 'N/A'}
                   </Typography>
                   {reply.creator?.id === user.details?.id && (
                     <IconButton
                       size="small"
                       onClick={() => handleDeleteReply(reply, index)}
                       style={{ marginLeft: 8 }}
                     >
                       <DeleteIcon fontSize="small" sx={{ color: 'white' }} />
                     </IconButton>
                   )}
                 </Box>
                 <Typography variant="body2">
                   {reply.reply}
                 </Typography>
               </Box>
             ))}
            
            {/* New Reply Input */}
            <Box mt={2}>
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                placeholder="Type your reply..."
                value={newReply}
                onChange={(e) => setNewReply(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={handleSendReply}
                      disabled={!newReply.trim()}
                    >
                      <SendIcon />
                    </IconButton>
                  ),
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseChat} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Quick Action Modal */}
        <Dialog
          open={quickActionModalOpen}
          onClose={() => {
            setQuickActionModalOpen(false);
            setSelectedQuickAction(null);
            setEditNoteText('');
            setNewNoteDueDate('');
            setSelectedNoteType('');
          }}
          maxWidth="sm"
          fullWidth
          className={classes.quickActionModal}
        >
          <DialogTitle>
            <Box display="flex" alignItems="center">
              {selectedQuickAction ? 
                React.createElement(selectedQuickAction.icon, { fontSize: "small" }) 
                : <AddIcon fontSize="small" />
              }
              <Typography variant="h6" style={{ marginLeft: 8 }}>
                {selectedQuickAction ? selectedQuickAction.display : 'New Note'}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedQuickAction && (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {selectedQuickAction.message}
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="note-type-label">Note Type</InputLabel>
                <Select
                  labelId="note-type-label"
                  id="note-type-select"
                  value={selectedNoteType || (selectedQuickAction ? selectedQuickAction.type : '')}
                  label="Note Type"
                  onChange={(e) => setSelectedNoteType(e.target.value)}
                  disabled={!!selectedQuickAction} // Disable if quick action is selected
                >
                  {apl.genericTypes?.noteTopic?.data && apl.genericTypes.noteTopic.data.length > 0 ? (
                    apl.genericTypes.noteTopic.data.map((topic) => (
                      <MenuItem key={topic.id} value={topic.description}>
                        {topic.description}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">Loading note types...</MenuItem>
                  )}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                type="date"
                label="Due Date"
                value={newNoteDueDate}
                onChange={(e) => setNewNoteDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Optional: Set a due date for this note"
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                placeholder={selectedQuickAction ? "Edit the note text before submitting..." : "Enter your note text..."}
                value={editNoteText}
                onChange={(e) => setEditNoteText(e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setQuickActionModalOpen(false);
              setSelectedQuickAction(null);
              setEditNoteText('');
              setNewNoteDueDate('');
              setSelectedNoteType('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleQuickActionSubmit} 
              color="primary"
              disabled={!editNoteText.trim()}
            >
              Create Note
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Note Modal */}
        <Dialog
          open={!!editingNote}
          onClose={() => setEditingNote(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Note</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              placeholder="Edit note text..."
              value={editNoteText}
              onChange={(e) => setEditNoteText(e.target.value)}
              style={{ marginTop: 16 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingNote(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEdit} 
              color="primary"
              disabled={!editNoteText.trim()}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Completed Status Modal */}
        <Dialog
          open={completedModalOpen}
          onClose={() => setCompletedModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedNoteForCompletion?.completed ? 'Mark Note as Incomplete' : 'Mark Note as Complete'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom>
              {selectedNoteForCompletion?.completed 
                ? 'This will mark the note as incomplete and allow you to set a due date.'
                : 'This will mark the note as completed and remove any due date.'
              }
            </Typography>
            {selectedNoteForCompletion?.completed && (
              <TextField
                fullWidth
                type="date"
                label="Due Date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={{ marginTop: 16 }}
                InputLabelProps={{ shrink: true }}
                helperText="Set a due date for this incomplete note"
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCompletedModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCompletedSubmit} 
              color="primary"
              disabled={selectedNoteForCompletion?.completed && !dueDate}
            >
              {selectedNoteForCompletion?.completed ? 'Mark Incomplete' : 'Mark Complete'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

AccountNotesTable.propTypes = {
  notes: PropTypes.object.isRequired,
  account: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  bills: PropTypes.object.isRequired,
  payments: PropTypes.object.isRequired,
  apl: PropTypes.object.isRequired,
  createNewNote: PropTypes.func.isRequired,
  createNewNoteReply: PropTypes.func.isRequired,
  deleteNoteOrReply: PropTypes.func.isRequired,
  updateNote: PropTypes.func.isRequired,
  setNotes: PropTypes.func.isRequired,
  getAccountNotesByAccountUUID: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  notes: state.notes,
  account: state.account,
  user: state.user,
  bills: state.bills,
  payments: state.payments,
  apl: state.apl,
});

const mapDispatchToProps = {
  createNewNote,
  createNewNoteReply,
  deleteNoteOrReply,
  updateNote,
  setNotes,
  getAccountNotesByAccountUUID,
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountNotesTable);
